'use client';

import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  NEW_PASSWORD_MIN_LENGTH,
  buttonStyle,
  errorStyle,
  formStyle,
  inputStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  titleStyle,
  useAccountErrorText,
} from './accountShared';
import { afterSignUpHref, newPasswordProblem, signUp } from './registration';
import { ActivationCodeStep, GuestAccountFields, TradeDocumentsUpload } from './applyAtSignUp';
import {
  TRADE_BUSINESS_TYPES,
  TRADE_FIELD_LIMITS,
  signInHrefFor,
  type TradeCommerceSource,
  type TradeDocumentType,
  type TradeDraftField,
  type TradeReferenceDraft,
} from './tradeApplication';
import { useTradeApplication } from './useTradeApplication';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Apply for a trade account — the whole of it, laid out before the applicant starts.
 *
 * <p>The form opens with what the shop will ask for (its requirements, read from the host), so
 * nobody is half way through before learning they need a certificate to hand. An application
 * belongs to an account: a visitor signs in, or makes an account here — the shop's own sign-up,
 * which signs them in at once — and the page comes back to the form. Signed in, the application
 * goes under the account's email address, which the form shows and does not let them change.
 *
 * <p>Then the business, its address, its registration (company number or UTR, VAT number or "not
 * VAT registered"), its trade body and membership number, its trade references, documents that
 * prove it trades (PDF, JPG or PNG), and a declaration that it is all true. What the shop requires
 * is marked and checked before sending; the shop checks it all again.
 *
 * <p>Where the shopper's application stands decides what shows instead: one being reviewed is
 * said instead of the form, an approved one says the trade account is open, and a refused one
 * says so, with the shop's reason when it gave one, above the form to apply again.
 *
 * <p>A shop that takes no applications — the feature is off unless the shop switches it on —
 * shows a sentence saying so and no form.
 */
export interface TradeApplicationFormProps {
  title?: Localized;
  intro?: Localized;
  signInHref?: string;
  accountHref?: string;
  termsHref?: string;
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

/** The value of the trade body list's "Other" choice, which opens a text box. */
const OTHER_BODY = '__other__';

export function TradeApplicationForm({
  title,
  intro,
  signInHref = '/sign-in',
  accountHref = '/account',
  termsHref = '/terms',
}: TradeApplicationFormProps) {
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');
  /**
   * The flow — whether the shop takes applications, who is signed in, the draft, the
   * references, the documents, sending it (with a sign-up for a guest) and everything that
   * follows — lives in {@link useTradeApplication}, so the Allow Energy theme's own trade
   * page runs exactly this logic under its own layout instead of a second copy of it.
   * This block owns the markup below and nothing else.
   */
  const {
    commerce,
    phase,
    viewer,
    requirements,
    needs,
    draft,
    setDraft,
    set,
    references,
    setReferences,
    documents,
    setDocuments,
    chosen,
    otherBody,
    setOtherBody,
    guest,
    setGuest,
    busy,
    error,
    sent,
    setSent,
    after,
    here,
    submit,
    afterSignedIn,
    pendingStatus,
    fieldLabel,
    problemText,
  } = useTradeApplication();

  if (!commerce?.submitTradeApplication) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="TradeApplicationForm"
            adapterName="CommerceSource"
            reason={t('commerce.trade.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  const label = (field: TradeDraftField, required: boolean) =>
    required ? fieldLabel(field) : `${fieldLabel(field)} ${t('commerce.trade.optional')}`;

  const heading = (
    <h1 {...part('title')} style={titleStyle}>
      {tx(title) || t('commerce.trade.title')}
    </h1>
  );

  const accountLink = (
    <div style={linkRowStyle}>
      <PortalLink href={accountHref}>{t('commerce.trade.goToAccount')}</PortalLink>
    </div>
  );

  const checklist = (
    <div {...part('checklist')} style={checklistStyle}>
      {/* Only a saving the shop worked out from its own prices, and only when it gave one. */}
      {requirements.savings && (
        <p {...part('savings')} style={{ margin: '0 0 12px', fontWeight: 600 }}>
          {t('commerce.trade.savings', {
            typical: requirements.savings.typicalPct,
            upTo: requirements.savings.upToPct,
            products: wholeNumber(requirements.savings.products, i18n?.language),
          })}
        </p>
      )}
      <h2 style={sectionTitleStyle}>{t('commerce.trade.checklist.title')}</h2>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
        <li>{t('commerce.trade.checklist.business')}</li>
        {needs('COMPANY_OR_TAX_REFERENCE') && <li>{t('commerce.trade.checklist.companyOrUtr')}</li>}
        {needs('VAT') && <li>{t('commerce.trade.checklist.vat')}</li>}
        {needs('TRADE_BODY') && <li>{t('commerce.trade.checklist.tradeBody')}</li>}
        {needs('REFERENCES') && (
          <li>{t('commerce.trade.checklist.references', { count: requirements.references })}</li>
        )}
        {needs('PROOF') && (
          <li>{t('commerce.trade.checklist.proof', { size: megabytes(requirements.maxBytes) })}</li>
        )}
      </ul>
      <p style={{ ...mutedStyle, margin: '8px 0 0', fontSize: 14 }}>{t('commerce.trade.checklist.review')}</p>
    </div>
  );

  let body: ReactNode;
  if (phase.kind === 'checking') {
    body = <p style={mutedStyle}>{t('commerce.trade.loading')}</p>;
  } else if (phase.kind === 'off') {
    body = (
      <p role="status" style={mutedStyle}>
        {t('commerce.trade.unavailable')}
      </p>
    );
  } else if (sent && sent.status !== 'REJECTED') {
    body =
      sent.status === 'APPROVED' ? (
        <>
          <p role="status" style={noticeStyle}>
            {t('commerce.trade.approved')}
          </p>
          {accountLink}
        </>
      ) : (
        <>
          <p role="status" style={noticeStyle}>
            {t('commerce.trade.sent', { email: sent.email })}
          </p>
          {accountLink}
        </>
      );
  } else if (after.kind === 'activate') {
    body = (
      <ActivationCodeStep
        commerce={commerce}
        email={after.email}
        password={after.password}
        idPrefix="trade"
        signInHref={signInHref}
        here={here}
        onSignedIn={() => afterSignedIn(after.email)}
      />
    );
  } else if (after.kind === 'documents') {
    const { email } = after;
    body = (
      <TradeDocumentsUpload
        commerce={commerce}
        requirements={requirements}
        initial={chosen}
        outstanding={after.status?.documentsOutstanding === true}
        onDone={(status) => setSent(status)}
        onSkip={() => setSent(after.status ?? pendingStatus(email))}
      />
    );
  } else if (after.kind === 'signIn') {
    body = (
      <>
        <p role="status" style={noticeStyle}>
          {t('commerce.signUpApply.activate.readySignIn')}
        </p>
        <div style={linkRowStyle}>
          <PortalLink href={signInHrefFor(signInHref, accountHref)}>{t('commerce.account.signIn')}</PortalLink>
        </div>
      </>
    );
  } else if (phase.existing?.status === 'PENDING') {
    body = (
      <>
        <p role="status" style={noticeStyle}>
          {t('commerce.trade.pending', {
            businessName: phase.existing.businessName,
            date: shortDate(phase.existing.submittedAt, i18n?.language),
            email: phase.existing.email,
          })}
        </p>
        {accountLink}
      </>
    );
  } else if (phase.existing?.status === 'APPROVED') {
    body = (
      <>
        <p role="status" style={noticeStyle}>
          {t('commerce.trade.approved')}
        </p>
        {accountLink}
      </>
    );
  } else if (!viewer && !commerce.register) {
    // A host that cannot make accounts: sign in, and come back to the form.
    body = (
      <>
        <p {...part('intro')} style={leadStyle}>
          {tx(intro) || t('commerce.trade.intro')}
        </p>
        {checklist}
        <AccountStep commerce={commerce} signInHref={signInHref} termsHref={termsHref} here={here} />
      </>
    );
  } else {
    const rejected = phase.existing?.status === 'REJECTED' ? phase.existing : null;
    body = (
      <>
        {rejected ? (
          <div role="status" style={{ ...outcomeStyle, marginBottom: 20 }}>
            <p style={{ margin: 0 }}>{t('commerce.trade.rejected', { businessName: rejected.businessName })}</p>
            {rejected.reason && (
              <p style={{ margin: '8px 0 0' }}>{t('commerce.trade.rejectedReason', { reason: rejected.reason })}</p>
            )}
            <p style={{ ...mutedStyle, margin: '8px 0 0' }}>{t('commerce.trade.applyAgain')}</p>
          </div>
        ) : (
          <p {...part('intro')} style={leadStyle}>
            {tx(intro) || t('commerce.trade.intro')}
          </p>
        )}
        {checklist}
        {/* The browser's own checks are off: they would refuse a website typed without https://,
            which the shop reads as well. Required fields are checked in submit() instead. */}
        <form onSubmit={(event) => void submit(event)} noValidate style={{ ...formStyle, maxWidth: 560, gap: 24 }}>
          {!viewer && (
            <GuestAccountFields value={guest} onChange={setGuest} idPrefix="trade" signInHref={signInHref} here={here} />
          )}
          <Section title={t('commerce.trade.sections.business')}>
            <AccountField
              id="trade-business-name"
              label={label('businessName', true)}
              value={draft.businessName}
              onChange={set('businessName')}
              autoComplete="organization"
              required
              maxLength={TRADE_FIELD_LIMITS.businessName}
            />
            <div style={fieldStyle}>
              <label htmlFor="trade-business-type" style={labelStyle}>
                {label('businessType', true)}
              </label>
              <select
                id="trade-business-type"
                value={draft.businessType}
                onChange={(event) => set('businessType')(event.target.value)}
                required
                style={inputStyle}
              >
                <option value="">{t('commerce.trade.chooseBusinessType')}</option>
                {TRADE_BUSINESS_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`commerce.trade.businessTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <AccountField
              id="trade-contact-name"
              label={label('contactName', true)}
              value={draft.contactName}
              onChange={set('contactName')}
              autoComplete="name"
              required
              maxLength={TRADE_FIELD_LIMITS.contactName}
            />
            <AccountField
              id="trade-email"
              label={label('email', true)}
              value={viewer ? draft.email : guest.email.trim().toLowerCase()}
              onChange={set('email')}
              type="email"
              autoComplete="email"
              readOnly
              maxLength={TRADE_FIELD_LIMITS.email}
              hint={t(viewer ? 'commerce.trade.emailFromAccount' : 'commerce.signUpApply.emailFromAbove')}
            />
            <AccountField
              id="trade-phone"
              label={label('phone', true)}
              value={draft.phone}
              onChange={set('phone')}
              type="tel"
              autoComplete="tel"
              required
              maxLength={TRADE_FIELD_LIMITS.phone}
            />
            <AccountField
              id="trade-website"
              label={label('website', false)}
              value={draft.website}
              onChange={set('website')}
              type="url"
              autoComplete="url"
              maxLength={TRADE_FIELD_LIMITS.website}
            />
          </Section>

          <Section title={t('commerce.trade.sections.address')}>
            <AccountField
              id="trade-address-line1"
              label={label('addressLine1', true)}
              value={draft.addressLine1}
              onChange={set('addressLine1')}
              autoComplete="address-line1"
              required
              maxLength={TRADE_FIELD_LIMITS.addressLine1}
            />
            <AccountField
              id="trade-address-line2"
              label={label('addressLine2', false)}
              value={draft.addressLine2}
              onChange={set('addressLine2')}
              autoComplete="address-line2"
              maxLength={TRADE_FIELD_LIMITS.addressLine2}
            />
            <div style={pairStyle}>
              <AccountField
                id="trade-city"
                label={label('city', true)}
                value={draft.city}
                onChange={set('city')}
                autoComplete="address-level2"
                required
                maxLength={TRADE_FIELD_LIMITS.city}
              />
              <AccountField
                id="trade-postcode"
                label={label('postcode', true)}
                value={draft.postcode}
                onChange={set('postcode')}
                autoComplete="postal-code"
                required
                maxLength={TRADE_FIELD_LIMITS.postcode}
              />
            </div>
          </Section>

          <Section
            title={t('commerce.trade.sections.registration')}
            note={needs('COMPANY_OR_TAX_REFERENCE') ? t('commerce.trade.companyOrUtrNote') : undefined}
          >
            <AccountField
              id="trade-company-number"
              label={label('companyNumber', false)}
              value={draft.companyNumber}
              onChange={set('companyNumber')}
              maxLength={TRADE_FIELD_LIMITS.companyNumber}
            />
            <AccountField
              id="trade-tax-reference"
              label={label('taxReference', false)}
              value={draft.taxReference}
              onChange={set('taxReference')}
              maxLength={TRADE_FIELD_LIMITS.taxReference}
              hint={t('commerce.trade.taxReferenceHint')}
            />
            <fieldset style={radioGroupStyle}>
              <legend style={labelStyle}>{label('vatRegistered', needs('VAT'))}</legend>
              {(['yes', 'no'] as const).map((answer) => (
                <label key={answer} style={radioStyle}>
                  <input
                    type="radio"
                    name="trade-vat-registered"
                    value={answer}
                    checked={draft.vatRegistered === answer}
                    onChange={() => setDraft((current) => ({ ...current, vatRegistered: answer }))}
                  />
                  {t(`commerce.trade.vatRegistered.${answer}`)}
                </label>
              ))}
            </fieldset>
            {draft.vatRegistered === 'yes' && (
              <AccountField
                id="trade-vat-number"
                label={label('vatNumber', true)}
                value={draft.vatNumber}
                onChange={set('vatNumber')}
                required
                maxLength={TRADE_FIELD_LIMITS.vatNumber}
              />
            )}
          </Section>

          <Section title={t('commerce.trade.sections.tradeBody')}>
            {requirements.tradeBodies.length > 0 ? (
              <div style={fieldStyle}>
                <label htmlFor="trade-body-choice" style={labelStyle}>
                  {label('tradeBody', needs('TRADE_BODY'))}
                </label>
                <select
                  id="trade-body-choice"
                  value={otherBody ? OTHER_BODY : draft.tradeBody}
                  onChange={(event) => {
                    const value = event.target.value;
                    setOtherBody(value === OTHER_BODY);
                    set('tradeBody')(value === OTHER_BODY ? '' : value);
                  }}
                  style={inputStyle}
                >
                  <option value="">{t('commerce.trade.chooseTradeBody')}</option>
                  {requirements.tradeBodies.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  <option value={OTHER_BODY}>{t('commerce.trade.otherTradeBody')}</option>
                </select>
              </div>
            ) : null}
            {(requirements.tradeBodies.length === 0 || otherBody) && (
              <AccountField
                id="trade-body"
                label={
                  requirements.tradeBodies.length === 0
                    ? label('tradeBody', needs('TRADE_BODY'))
                    : t('commerce.trade.otherTradeBodyName')
                }
                value={draft.tradeBody}
                onChange={set('tradeBody')}
                maxLength={TRADE_FIELD_LIMITS.tradeBody}
              />
            )}
            <AccountField
              id="trade-body-number"
              label={label('tradeBodyNumber', needs('TRADE_BODY'))}
              value={draft.tradeBodyNumber}
              onChange={set('tradeBodyNumber')}
              maxLength={TRADE_FIELD_LIMITS.tradeBodyNumber}
            />
          </Section>

          {(needs('REFERENCES') || references.length > 0) && (
            <Section
              title={t('commerce.trade.sections.references')}
              note={t('commerce.trade.referencesNote', { count: requirements.references })}
            >
              {references.map((reference, index) => (
                <ReferenceFields
                  key={index}
                  index={index}
                  value={reference}
                  onChange={(next) =>
                    setReferences((current) => current.map((item, at) => (at === index ? next : item)))
                  }
                />
              ))}
            </Section>
          )}

          <Section
            title={label('documents', needs('PROOF'))}
            note={
              viewer
                ? t('commerce.trade.documentsNote', { size: megabytes(requirements.maxBytes) })
                : `${t('commerce.trade.documentsNote', { size: megabytes(requirements.maxBytes) })} ${t('commerce.signUpApply.trade.documentsLater')}`
            }
          >
            {documents.map((row, index) => (
              <div key={row.key} style={documentRowStyle}>
                <div style={fieldStyle}>
                  <label htmlFor={`trade-document-type-${row.key}`} style={labelStyle}>
                    {t('commerce.trade.documentType')}
                  </label>
                  <select
                    id={`trade-document-type-${row.key}`}
                    value={row.type}
                    onChange={(event) =>
                      setDocuments((current) =>
                        current.map((item) =>
                          item.key === row.key ? { ...item, type: event.target.value as TradeDocumentType | '' } : item,
                        ),
                      )
                    }
                    style={inputStyle}
                  >
                    <option value="">{t('commerce.trade.chooseDocumentType')}</option>
                    {requirements.documentTypes.map((type) => (
                      <option key={type} value={type}>
                        {t(`commerce.trade.documentTypes.${type}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label htmlFor={`trade-document-file-${row.key}`} style={labelStyle}>
                    {t('commerce.trade.documentFile', { number: index + 1 })}
                  </label>
                  <input
                    id={`trade-document-file-${row.key}`}
                    type="file"
                    accept={ACCEPT}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      setDocuments((current) => current.map((item) => (item.key === row.key ? { ...item, file } : item)));
                    }}
                  />
                </div>
                {documents.length > 1 && (
                  <button
                    type="button"
                    style={{ ...secondaryButtonStyle, justifySelf: 'start' }}
                    onClick={() => setDocuments((current) => current.filter((item) => item.key !== row.key))}
                  >
                    {t('commerce.trade.removeDocument')}
                  </button>
                )}
              </div>
            ))}
            {documents.length < requirements.maxDocuments && (
              <button
                type="button"
                style={{ ...secondaryButtonStyle, justifySelf: 'start' }}
                onClick={() =>
                  setDocuments((current) => [
                    ...current,
                    { key: Math.max(0, ...current.map((item) => item.key)) + 1, type: '', file: null },
                  ])
                }
              >
                {t('commerce.trade.addDocument')}
              </button>
            )}
          </Section>

          <Section title={t('commerce.trade.sections.anythingElse')}>
            <TradeTextArea
              id="trade-message"
              label={label('message', false)}
              value={draft.message}
              onChange={set('message')}
              rows={4}
              maxLength={TRADE_FIELD_LIMITS.message}
            />
          </Section>

          <label style={declarationStyle}>
            <input
              type="checkbox"
              checked={draft.declared}
              onChange={(event) => setDraft((current) => ({ ...current, declared: event.target.checked }))}
            />
            <span>
              {t('commerce.trade.declaration')}{' '}
              <PortalLink href={termsHref}>{t('commerce.trade.termsLink')}</PortalLink>
            </span>
          </label>

          {error && (
            <div role="alert" style={errorStyle}>
              {error.text}
              {error.kind === 'ALREADY_TRADE' && (
                <>
                  {' '}
                  <PortalLink href={accountHref}>{t('commerce.trade.goToAccount')}</PortalLink>
                </>
              )}
            </div>
          )}
          <button type="submit" disabled={busy} style={buttonStyle}>
            {busy
              ? t('commerce.trade.submitting')
              : viewer
                ? t('commerce.trade.submit')
                : t('commerce.signUpApply.submit')}
          </button>
        </form>
      </>
    );
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 680 }}>
        {heading}
        {body}
      </div>
    </BlockRoot>
  );
}

/**
 * The account an application belongs to, for a visitor who is not signed in: sign in and come
 * back, or make one here with the shop's own sign-up. A sign-up that opens the account at once
 * reloads this page, signed in, at the form; one that waits for an emailed code says so and
 * brings them back here once they have confirmed.
 *
 * <p>Shared with the installer application, which says its own lead and confirmation in place of
 * the trade account's and prefixes the field ids with `idPrefix`.
 */
export function AccountStep({
  commerce,
  signInHref,
  termsHref,
  here,
  lead,
  confirmEmail,
  idPrefix = 'trade',
}: {
  commerce: TradeCommerceSource;
  signInHref: string;
  termsHref: string;
  here: string | null;
  lead?: string;
  confirmEmail?: (email: string) => string;
  idPrefix?: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const problem = newPasswordProblem(password, confirmPassword);
    if (problem) {
      setError(t(`commerce.account.errors.${problem}`));
      return;
    }
    const address = email.trim().toLowerCase();
    setBusy(true);
    setError(null);
    try {
      const outcome = await signUp(commerce, {
        email: address,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        confirmPassword,
      });
      const href = afterSignUpHref(outcome, { next: here, successHref: here ?? '/', signInHref });
      if (href) {
        // A full load, as after signing in, so every block — this one too — reads who is signed in.
        window.location.assign(href);
        return;
      }
      setSentTo(address);
    } catch (failure) {
      setError(errorText(failure));
    }
    setBusy(false);
  }

  if (sentTo) {
    return (
      <p role="status" style={noticeStyle}>
        {confirmEmail ? confirmEmail(sentTo) : t('commerce.trade.account.confirmEmail', { email: sentTo })}
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={outcomeStyle}>
        <h2 style={sectionTitleStyle}>{t('commerce.trade.account.title')}</h2>
        <p style={{ margin: '0 0 8px' }}>{lead ?? t('commerce.trade.account.lead')}</p>
        <PortalLink href={signInHrefFor(signInHref, here)}>{t('commerce.trade.account.signIn')}</PortalLink>
      </div>
      {commerce.register && (
        <form onSubmit={(event) => void create(event)} style={{ ...formStyle, maxWidth: 560 }}>
          <h2 style={sectionTitleStyle}>{t('commerce.trade.account.createTitle')}</h2>
          {error && (
            <p role="alert" style={errorStyle}>
              {error}
            </p>
          )}
          <div style={pairStyle}>
            <AccountField
              id={`${idPrefix}-account-first-name`}
              label={t('commerce.account.firstName')}
              value={firstName}
              onChange={setFirstName}
              autoComplete="given-name"
              required
            />
            <AccountField
              id={`${idPrefix}-account-last-name`}
              label={t('commerce.account.lastName')}
              value={lastName}
              onChange={setLastName}
              autoComplete="family-name"
              required
            />
          </div>
          <AccountField
            id={`${idPrefix}-account-email`}
            label={t('commerce.account.email')}
            value={email}
            onChange={setEmail}
            type="email"
            autoComplete="email"
            required
          />
          <AccountField
            id={`${idPrefix}-account-password`}
            label={t('commerce.account.password')}
            value={password}
            onChange={setPassword}
            type="password"
            autoComplete="new-password"
            required
            hint={t('commerce.account.passwordRuleHint', { count: NEW_PASSWORD_MIN_LENGTH })}
          />
          <AccountField
            id={`${idPrefix}-account-confirm-password`}
            label={t('commerce.account.confirmPassword')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            type="password"
            autoComplete="new-password"
            required
          />
          <p style={{ ...mutedStyle, fontSize: 13 }}>
            {t('commerce.trade.termsNotice')} <PortalLink href={termsHref}>{t('commerce.trade.termsLink')}</PortalLink>
          </p>
          <button type="submit" disabled={busy} style={buttonStyle}>
            {busy ? t('commerce.trade.account.creating') : t('commerce.trade.account.create')}
          </button>
        </form>
      )}
    </div>
  );
}

function ReferenceFields({
  index,
  value,
  onChange,
}: {
  index: number;
  value: TradeReferenceDraft;
  onChange: (value: TradeReferenceDraft) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const set = (field: keyof TradeReferenceDraft) => (text: string) => onChange({ ...value, [field]: text });
  const id = (field: string) => `trade-reference-${index}-${field}`;
  return (
    <fieldset style={referenceStyle}>
      <legend style={labelStyle}>{t('commerce.trade.referenceTitle', { number: index + 1 })}</legend>
      <AccountField
        id={id('company')}
        label={t('commerce.trade.reference.company')}
        value={value.company}
        onChange={set('company')}
        maxLength={TRADE_FIELD_LIMITS.businessName}
      />
      <AccountField
        id={id('contact')}
        label={t('commerce.trade.reference.contactName')}
        value={value.contactName}
        onChange={set('contactName')}
        maxLength={TRADE_FIELD_LIMITS.contactName}
      />
      <div style={pairStyle}>
        <AccountField
          id={id('email')}
          label={t('commerce.trade.reference.email')}
          value={value.email}
          onChange={set('email')}
          type="email"
          maxLength={TRADE_FIELD_LIMITS.email}
        />
        <AccountField
          id={id('phone')}
          label={t('commerce.trade.reference.phone')}
          value={value.phone}
          onChange={set('phone')}
          type="tel"
          maxLength={TRADE_FIELD_LIMITS.phone}
        />
      </div>
      <AccountField
        id={id('relationship')}
        label={t('commerce.trade.reference.relationship')}
        value={value.relationship}
        onChange={set('relationship')}
        maxLength={TRADE_FIELD_LIMITS.relationship}
        hint={t('commerce.trade.reference.relationshipHint')}
      />
    </fieldset>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <fieldset style={sectionStyle}>
      <legend style={sectionTitleStyle}>{title}</legend>
      {note && <p style={{ ...mutedStyle, margin: 0, fontSize: 14 }}>{note}</p>}
      {children}
    </fieldset>
  );
}

function TradeTextArea({
  id,
  label,
  value,
  onChange,
  rows,
  maxLength,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
  maxLength: number;
  autoComplete?: string;
}) {
  return (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        maxLength={maxLength}
        autoComplete={autoComplete}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </div>
  );
}

/** Bytes as whole megabytes, as a limit is said: 26 214 400 is "25". */
function megabytes(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}

/** A count as the shopper reads one: 1,240. */
function wholeNumber(value: number, language: string | undefined): string {
  try {
    return new Intl.NumberFormat(language || 'en').format(value);
  } catch {
    return String(value);
  }
}

/** A date as the shopper reads one, or the ISO day when the browser cannot format it. */
function shortDate(value: string, language: string | undefined): string {
  try {
    return new Intl.DateTimeFormat(language || 'en', { dateStyle: 'long' }).format(new Date(value));
  } catch {
    return value.slice(0, 10);
  }
}

const fieldStyle: CSSProperties = { display: 'grid', gap: 4 };
const labelStyle: CSSProperties = { fontSize: 14, fontWeight: 500 };
const sectionTitleStyle: CSSProperties = { margin: '0 0 8px', fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600 };
const sectionStyle: CSSProperties = { display: 'grid', gap: 12, margin: 0, padding: 0, border: 0, minWidth: 0 };
const pairStyle: CSSProperties = { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' };
const radioGroupStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 16, margin: 0, padding: 0, border: 0 };
const radioStyle: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15 };
const referenceStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
  margin: 0,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
  minWidth: 0,
};
const documentRowStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
};
const declarationStyle: CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15 };
const outcomeStyle: CSSProperties = {
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  background: 'var(--color-surface-muted, #f3f4f6)',
};
const checklistStyle: CSSProperties = { ...outcomeStyle, marginBottom: 24 };
